import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import screenfull from "screenfull";
// import PropTypes from "prop-types";
import { markLessonCompletedAction } from "@redux/modules/course/slices";
import { SECTION_ITEM_TYPE } from "@constants";

const initialState = {
  playing: false,
  muted: false,
  volume: 1,
  playbackRate: 1,
  played: 0,
  playedSeconds: 0,
  seeking: false,
  isFullscreen: false,
  showNextBtn: false,
};

const useVideoControl = (lecture, hasNext, handleNext) => {
  const [state, setState] = useState(initialState);
  const dispatch = useDispatch();

  const playerRef = useRef(null);
  const playerContainerRef = useRef(null);

  useEffect(() => {
    setState({ ...state, played: 0, playedSeconds: 0 });
    return () => {
      if (playerRef.current) {
        playerRef.current.seekTo(0);
      }
    };
  }, [lecture]);

  // Logics
  const handlePlayPause = () => {
    setState({ ...state, playing: !state.playing });
  };

  const handleEnd = () => {
    setState({ ...state, playing: false, showNextBtn: hasNext ? true : false });
    if (!lecture.isCompleted) {
      dispatch(
        markLessonCompletedAction({
          id: lecture.id,
          type: SECTION_ITEM_TYPE.LECTURE,
        })
      );
    }
  };

  const handleClickNext = () => {
    setState({ ...state, showNextBtn: false });
    handleNext();
  };

  const handleRewind = () => {
    playerRef.current.seekTo(playerRef.current.getCurrentTime() - 5);
  };

  const handleFastForward = () => {
    playerRef.current.seekTo(playerRef.current.getCurrentTime() + 5);
  };

  const handleMuted = () => {
    if (state.volume === 0) {
      setState({ ...state, volume: 1, muted: false });
      return;
    }
    setState({ ...state, muted: !state.muted });
  };

  const handleVolumeChange = (newVolume) => {
    setState({
      ...state,
      volume: parseFloat(newVolume / 100),
      muted: newVolume === 0,
    });
  };

  const handlePlaybackRateChange = (newRate) => {
    setState({
      ...state,
      playbackRate: newRate,
    });
  };

  const toggleFullScreen = () => {
    screenfull.toggle(playerContainerRef.current);
    setState({
      ...state,
      isFullscreen: !screenfull.isFullscreen,
    });
  };

  const handleProgress = (progress) => {
    if (!state.seeking) {
      setState({ ...state, ...progress });
    }
  };

  const handleChangeSeek = (newSeek) => {
    setState({ ...state, played: parseFloat(newSeek / 1000) });
  };

  const handleSeekMouseDown = () => {
    setState({ ...state, seeking: true });
  };

  const handleSeekMouseUp = (newSeek) => {
    setState({ ...state, seeking: false });
    playerRef.current.seekTo(newSeek / 1000);
  };

  return {
    state,
    playerRef,
    playerContainerRef,
    handlePlayPause,
    handleRewind,
    handleFastForward,
    handleMuted,
    handleVolumeChange,
    handlePlaybackRateChange,
    toggleFullScreen,
    handleProgress,
    handleChangeSeek,
    handleSeekMouseDown,
    handleSeekMouseUp,
    handleEnd,
    handleClickNext,
  };
};

/* required: display name */
useVideoControl.displayName = "useVideoControl";

/* required: props types */
useVideoControl.propTypes = {};

/* required: default props */
useVideoControl.defaultProps = {};

export { useVideoControl };
